import React, { useEffect, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LiveTrackingMapProps {
  jobLat: number;
  jobLng: number;
  homeeLat: number | null;
  homeeLng: number | null;
  homeeName: string | null;
}

// Web-only implementation — react-native-maps has no web target, so this
// renders directly into a DOM node via Leaflet (react-native-web mounts
// real DOM elements, so a plain ref + Leaflet works fine here).
export const LiveTrackingMap: React.FC<LiveTrackingMapProps> = ({ jobLat, jobLng, homeeLat, homeeLng, homeeName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const homeeMarkerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current).setView([jobLat, jobLng], 14);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    L.marker([jobLat, jobLng], {
      icon: L.divIcon({ className: 'homee-job-pin', html: '📍', iconSize: [24, 24] }),
    }).addTo(map).bindPopup('Místo zakázky');

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [jobLat, jobLng]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || homeeLat == null || homeeLng == null) return;

    if (!homeeMarkerRef.current) {
      homeeMarkerRef.current = L.marker([homeeLat, homeeLng], {
        icon: L.divIcon({ className: 'homee-provider-pin', html: '🚗', iconSize: [24, 24] }),
      }).addTo(map).bindPopup(homeeName ?? 'HoMee');
    } else {
      homeeMarkerRef.current.setLatLng([homeeLat, homeeLng]);
    }

    const bounds = L.latLngBounds([[jobLat, jobLng], [homeeLat, homeeLng]]);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [homeeLat, homeeLng, homeeName, jobLat, jobLng]);

  return <View style={styles.container}><div ref={containerRef} style={{ width: '100%', height: '100%' }} /></View>;
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: 260,
    borderRadius: 12,
    overflow: 'hidden',
  },
});
